import React, {
	useState,
	useMemo,
	useCallback,
	useRef,
	useLayoutEffect,
	useEffect,
} from "react";
import {
	VariableSizeList as List,
	ListChildComponentProps,
} from "react-window";
import { TablePagination } from "./table-pagination";
import { Column, Sort } from "./index";
import { Box, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';

interface Props<I> {
	[key: string]: any;
	items: I[];
	columns: Column<I>[];
	hidePagination?: boolean;
	page?: number;
	onPageChange?: (page: number) => void;
	pageSize?: number;
	onPageSizeChange?: (pageSize: number) => void;
	sort?: Sort;
	onSortChange?: (sort: Sort) => void;
	isSelected?: (item: I) => boolean;
	onSelect?: (items: I[]) => void;
	onUnselect?: (items: I[]) => void;
	rowHeight?: number;
	height?: number;
	fixedHeight?: number;
	disableDynamicRowHeight?: boolean;
}

const getPageSizeOptions = (pageSize: number) => {
	const baseOptions = [5, 10, 20, 25, 50, 100];
	const extraOptions = [250, 500];
	const options = [...baseOptions];

	extraOptions.forEach((opt) => {
		if (pageSize > 100 || pageSize === opt) {
			options.push(opt);
		}
	});

	return options.map((value) => ({
		value,
		label: `${value} rows`,
	}));
};

const DEFAULT_COL_WIDTH = 230;

const renderTitleCell = <I,>(
	column: Column<I>,
	index: number,
	sort: Sort | undefined,
	onSortChange: (sort: Sort) => void
) => {
	const theme = useTheme();
	return (
		<Box
			key={index}
			component="div"
			style={{
				width: column.width ?? DEFAULT_COL_WIDTH,
				minWidth: column.width ?? DEFAULT_COL_WIDTH,
				maxWidth: column.width ?? DEFAULT_COL_WIDTH,
				fontWeight: "bold",
				paddingLeft: theme.spacing(2),
				paddingRight: theme.spacing(2),
				paddingTop: theme.spacing(1),
				paddingBottom: theme.spacing(1),
				textAlign: column.align ?? "left",
				cursor: column.sort ? "pointer" : "default",
				backgroundColor: theme.palette.background.paper,
				borderRight: `1px solid ${theme.palette.divider}`,
				boxSizing: "border-box",
				flexShrink: 0,
				flexGrow: 0,
			}}
			onClick={() => {
				if (column.sort && column.id) {
					const desc = sort && sort.id === column.id ? !sort.desc : false;
					onSortChange({ id: column.id, desc });
				}
			}}
		>
			{column.title}
			{column.sort && sort?.id === column.id && (
				<Box
					component="span"
					style={{ marginLeft: theme.spacing(0.5) }}
				>
					{sort?.desc ? "▼" : "▲"}
				</Box>
			)}
		</Box>
	);
};

export function VirtualizedTable<I>(props: Props<I>) {
	const {
		items: propItems,
		columns: propColumns,
		hidePagination,
		page: propPage,
		onPageChange,
		pageSize: propPageSize,
		onPageSizeChange,
		sort: propSort,
		onSortChange,
		isSelected,
		onSelect,
		onUnselect,
		rowHeight = 60,
		fixedHeight = 590,
		disableDynamicRowHeight = false,
	} = props;

	const theme = useTheme();

	const [internalPage, setInternalPage] = useState(0);
	const [internalPageSize, setInternalPageSize] = useState(10);
	const [internalSort, setInternalSort] = useState<Sort | undefined>(undefined);

	const page = propPage ?? internalPage;
	const pageSize = propPageSize ?? internalPageSize;
	const sort = propSort ?? internalSort;

	const columns = useMemo(
		() => propColumns.filter((column) => !column.hidden),
		[propColumns]
	);
	const pageSizeOptions = useMemo(
		() => getPageSizeOptions(pageSize),
		[pageSize]
	);

	const sortedItems = useMemo(() => {
		let items = [...propItems];
		if (sort) {
			const column = columns.find((column) => column.id === sort.id);
			if (column && column.sort) {
				items = items.sort((a, b) => column.sort!(a, b) * (sort.desc ? -1 : 1));
			}
		}
		return items;
	}, [propItems, columns, sort]);

	const subItems = useMemo(
		() => sortedItems.slice(page * pageSize, page * pageSize + pageSize),
		[sortedItems, page, pageSize]
	);

	const handlePageChange = useCallback(
		(_event: unknown, newPage: number) => {
			if (onPageChange) {
				onPageChange(newPage);
			} else {
				setInternalPage(newPage);
			}
		},
		[onPageChange]
	);

	const handlePageSizeChange = useCallback(
		(event: React.ChangeEvent<HTMLInputElement>) => {
			const newSize = parseInt(event.target.value, 10);
			if (onPageSizeChange) {
				onPageSizeChange(newSize);
			} else {
				setInternalPageSize(newSize);
			}
		},
		[onPageSizeChange]
	);

	const handleSortChange = useCallback(
		(newSort: Sort) => {
			if (onSortChange) {
				onSortChange(newSort);
			} else {
				setInternalSort(newSort);
			}
		},
		[onSortChange]
	);

	const totalWidth = columns.reduce(
		(sum, col) =>
			sum + (typeof col.width === "number" ? col.width : DEFAULT_COL_WIDTH),
		isSelected && onSelect && onUnselect ? 70 : 0
	);

	const HeaderRow = ({ style }: { style?: React.CSSProperties }) => (
		<Box
			style={{
				...style,
				width: totalWidth,
				minWidth: totalWidth,
				backgroundColor: theme.palette.background.paper,
				display: "flex",
				alignItems: "center",
				paddingLeft: theme.spacing(2),
				paddingRight: theme.spacing(2),
				paddingTop: theme.spacing(1),
				paddingBottom: theme.spacing(1),
				borderBottom: `1px solid ${theme.palette.divider}`,
			}}
		>
			{isSelected && onSelect && onUnselect && (
				<Box style={{ width: 70, minWidth: 70, maxWidth: 70 }} />
			)}
			{columns.map((column, index) =>
				renderTitleCell(
					{ ...column, width: column.width ?? DEFAULT_COL_WIDTH },
					index,
					sort,
					handleSortChange
				)
			)}
		</Box>
	);

	const rowHeights = useRef<{ [key: number]: number }>({});

	const setRowHeight = (index: number, size: number) => {
		if (rowHeights.current[index] !== size) {
			rowHeights.current = { ...rowHeights.current, [index]: size };
			if (listRef.current) {
				listRef.current.resetAfterIndex(index);
			}
		}
	};

	const getRowHeight = (index: number) => {
		if (disableDynamicRowHeight) {
			return rowHeight;
		}
		return rowHeights.current[index] || rowHeight;
	};

	const listRef = useRef<any>(null);

	const containerRef = useRef<HTMLDivElement>(null);
	const [tableSize, setTableSize] = useState({ width: 0, height: window.innerHeight - fixedHeight });

	useEffect(() => {
		function handleResize() {
			const width = containerRef.current?.clientWidth ?? 0;
			const height = window.innerHeight - fixedHeight;
			setTableSize({ width, height });
		}
		handleResize();
		window.addEventListener("resize", handleResize);
		return () => window.removeEventListener("resize", handleResize);
	}, []);

	const Row = ({ index, style }: ListChildComponentProps) => {
		const ref = useRef<HTMLDivElement>(null);

		useLayoutEffect(() => {
			if (disableDynamicRowHeight) return;
			if (index === 0) return;
			if (!ref.current || typeof ResizeObserver === "undefined") return;

			const observer = new ResizeObserver((entries) => {
				try {
					const entry = entries[0];
					const rawHeight = Math.round(entry.contentRect.height);
					const extra = parseInt(theme.spacing(2), 10);
					const measured = rawHeight + extra;
					if (rowHeights.current[index] !== measured) {
						requestAnimationFrame(() => {
							setRowHeight(index, measured);
						});
					}
				} catch(error) {
				 //	Handle error
				} finally {
					observer.disconnect();
				}
			});
			observer.observe(ref.current);

			return () => {
				observer.disconnect();
			};
		}, [disableDynamicRowHeight, index, subItems, columns]);

		if (index === 0) {
			return <HeaderRow style={style} key={index} />;
		}
		const item = subItems[index - 1];
		if (!item) return null;
		return (
			<div
				style={{
					...style,
					width: totalWidth,
					minWidth: totalWidth,
					backgroundColor: index % 2 === 0
						? theme.palette.action.hover
						: theme.palette.background.default,
					borderBottom: `1px solid ${theme.palette.divider}`,
				}}
				key={index}
			>
				<div
					ref={ref}
					style={{
						display: "flex",
						alignItems: "center",
						width: totalWidth,
						paddingLeft: theme.spacing(2),
						paddingRight: theme.spacing(2),
						paddingTop: theme.spacing(1),
						paddingBottom: theme.spacing(1),
					}}
				>
					{isSelected && onSelect && onUnselect && (
						<Box
							style={{
								width: 70,
								minWidth: 70,
								maxWidth: 70,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
							}}
						>
							<input
								type="checkbox"
								checked={isSelected(item)}
								onChange={() => {
									if (isSelected(item)) {
										onUnselect([item]);
									} else {
										onSelect([item]);
									}
								}}
							/>
						</Box>
					)}
					{columns.map((column, colIndex) => (
						<Box
							key={colIndex}
							style={{
								width: column.width ?? DEFAULT_COL_WIDTH,
								minWidth: column.width ?? DEFAULT_COL_WIDTH,
								maxWidth: column.width ?? DEFAULT_COL_WIDTH,
								paddingLeft: theme.spacing(2),
								paddingRight: theme.spacing(2),
								textAlign: column.align ?? "left",
								overflowWrap: "break-word",
								wordBreak: "break-word",
								whiteSpace: "pre-line",
								boxSizing: "border-box",
								flexShrink: 0,
								flexGrow: 0,
								height: 'inherit',
							}}
						>
							{column.showOnHover ? (
								<div className="showOnHover">
									{column.value(item, colIndex, index - 1)}
								</div>
							) : (
								column.value(item, colIndex, index - 1)
							)}
						</Box>
					))}
				</div>
			</div>
		);
	};

	return (
		<Paper
			ref={containerRef}
			elevation={1}
			style={{
				width: "100%",
				height: "calc(100vh - 540px)",
			}}
		>
			<div style={{ width: "100%" }}>
				<List
					ref={listRef}
					height={tableSize.height}
					width={tableSize.width}
					itemCount={subItems.length + 1}
					itemSize={getRowHeight}
					style={{ overflowY: "scroll", overflowX: "auto" }}
				>
					{Row}
				</List>
			</div>
			{!!!hidePagination && (
				<div>
					<TablePagination
						count={propItems.length}
						page={page}
						rowsPerPage={pageSize}
						rowsPerPageOptions={pageSizeOptions}
						onPageChange={handlePageChange}
						onRowsPerPageChange={handlePageSizeChange}
					/>
				</div>
			)}
		</Paper>
	);
}
